import { useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import SignatureModal from '../components/Dashboard/SignatureModal';
import './SharedDocument.css';

import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

export default function SharedDocument() {
  const { token }               = useParams();
  const [password, setPassword] = useState('');
  const [meta, setMeta]         = useState(null);
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [showSig, setShowSig]   = useState(false);
  const [signed, setSigned]     = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const pdfDocRef = useRef(null);
  const canvasRef = useRef(null);

  const renderPage = useCallback(async (pdf, pageNum) => {
    const page     = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.4 });
    const canvas   = canvasRef.current;
    canvas.width   = viewport.width;
    canvas.height  = viewport.height;
    page.render({ canvasContext: canvas.getContext('2d'), viewport });
  }, []);

  const loadPdf = useCallback(async (pw) => {
    const res = await fetch(`/api/documents/shared/${token}/pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: pw }),
    });
    if (!res.ok) return;
    const buf = await res.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    pdfDocRef.current = pdf;
    setTotalPages(pdf.numPages);
    await renderPage(pdf, 1);
    setCurrentPage(1);
  }, [token, renderPage]);

  const unlock = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`/api/documents/shared/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMeta(data);
      setSigned(!!data.recipient_signed);
      await loadPdf(password);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const changePage = async (dir) => {
    const next = currentPage + dir;
    if (next < 1 || next > totalPages) return;
    setCurrentPage(next);
    await renderPage(pdfDocRef.current, next);
  };

  const handleSign = async (signatureBase64) => {
    setShowSig(false);
    const res = await fetch(`/api/documents/shared/${token}/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password, signatureBase64 }),
    });
    if (res.ok) {
      setSigned(true);
      await loadPdf(password);
    } else {
      const d = await res.json().catch(() => ({}));
      alert(d.error || 'Signing failed');
    }
  };

  const alreadySigned = signed || meta?.recipient_signed;
  const canSign       = meta && !alreadySigned && meta.status !== 'draft';

  return (
    <div className="shared-doc-page">
      {!meta ? (
        <div className="shared-doc-card">
          <div className="shared-doc-logo">Özel<span>.</span></div>
          <h2>Secure Document</h2>
          <p>Enter the password to access this document.</p>
          <form onSubmit={unlock}>
            <input
              className="shared-pw-input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            {error && <p className="shared-error">{error}</p>}
            <button className="shared-submit-btn" type="submit" disabled={loading}>
              {loading ? 'Checking…' : 'Open Document'}
            </button>
          </form>
        </div>
      ) : (
        <div className="shared-viewer">
          <div className="shared-viewer-toolbar">
            <span className="doc-name">{meta.filename}</span>
            {alreadySigned && <span className="signed-badge">✓ Signed</span>}
            {canSign && (
              <button className="shared-sign-btn" onClick={() => setShowSig(true)}>
                Sign Document
              </button>
            )}
          </div>
          <div className="shared-canvas-wrap">
            <canvas ref={canvasRef} />
          </div>
          {totalPages > 1 && (
            <div className="shared-page-nav">
              <button disabled={currentPage <= 1} onClick={() => changePage(-1)}>‹ Prev</button>
              <span>Page {currentPage} of {totalPages}</span>
              <button disabled={currentPage >= totalPages} onClick={() => changePage(1)}>Next ›</button>
            </div>
          )}
        </div>
      )}
      {showSig && <SignatureModal onConfirm={handleSign} onClose={() => setShowSig(false)} />}
    </div>
  );
}
